import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session_id = searchParams.get('session_id');
  const { clearCart } = useCartStore();
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Überprüfe Zahlungsstatus...');
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (session_id) {
      pollPaymentStatus(session_id);
    } else {
      setStatus('error');
      setMessage('Keine Session-ID gefunden');
    }
  }, [session_id]);

  const pollPaymentStatus = async (sessionId, attemptNum = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attemptNum >= maxAttempts) {
      setStatus('error');
      setMessage('Zeitüberschreitung beim Überprüfen des Zahlungsstatus. Bitte überprüfen Sie Ihre E-Mails für die Bestätigung.');
      return;
    }

    try {
      setAttempts(attemptNum + 1);
      const response = await axiosInstance.get(`/stripe/checkout-status/${sessionId}`);

      if (response.data.payment_status === 'paid') {
        // Payment successful - create order
        await createOrderFromPayment(sessionId, response.data);
        setStatus('success');
        setMessage('Zahlung erfolgreich! Ihre Bestellung wurde aufgegeben.');
        clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        setMessage('Die Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut.');
        return;
      }

      // If payment is still pending, continue polling
      setMessage(`Überprüfe Zahlungsstatus... (Versuch ${attemptNum + 1}/${maxAttempts})`);
      setTimeout(() => pollPaymentStatus(sessionId, attemptNum + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setMessage('Fehler beim Überprüfen des Zahlungsstatus. Bitte kontaktieren Sie den Support.');
    }
  };

  const createOrderFromPayment = async (sessionId, paymentData) => {
    try {
      // Get transaction details
      const transaction = await axiosInstance.get(`/payment-transactions/${sessionId}`);
      
      if (transaction.data && transaction.data.metadata) {
        const metadata = transaction.data.metadata;
        const orderItems = JSON.parse(metadata.order_items || '[]');
        const shippingAddress = JSON.parse(metadata.shipping_address || '{}');

        // Create order
        const orderResponse = await axiosInstance.post('/orders', {
          items: orderItems,
          payment_method: 'stripe',
          shipping_address: shippingAddress,
          payment_session_id: sessionId
        });

        setOrderData(orderResponse.data);
      }
    } catch (error) {
      console.error('Error creating order:', error);
      // Don't fail the success page if order creation fails
      // The payment was successful
    }
  };

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {status === 'checking' && 'Überprüfe Zahlung...'}
              {status === 'success' && 'Zahlung erfolgreich!'}
              {status === 'error' && 'Fehler'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            {status === 'checking' && (
              <>
                <Loader2 className="h-16 w-16 animate-spin mx-auto text-blue-600" />
                <p className="text-gray-600">{message}</p>
                <p className="text-sm text-gray-500">
                  Bitte schließen Sie diese Seite nicht...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="h-16 w-16 mx-auto text-green-600" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Vielen Dank für Ihre Bestellung!
                  </h3>
                  <p className="text-gray-600">{message}</p>
                  {orderData && (
                    <p className="text-sm text-gray-500 mt-2">
                      Bestellnummer: #{orderData.id?.slice(0, 8)}
                    </p>
                  )}
                </div>
                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/orders')}
                    className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
                  >
                    Meine Bestellungen ansehen
                  </Button>
                  <Button 
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="w-full"
                  >
                    Zurück zur Startseite
                  </Button>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-16 w-16 mx-auto text-red-600" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Etwas ist schiefgelaufen
                  </h3>
                  <p className="text-gray-600">{message}</p>
                </div>
                <div className="space-y-3">
                  <Button 
                    onClick={() => navigate('/checkout')}
                    className="w-full bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
                  >
                    Erneut versuchen
                  </Button>
                  <Button 
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="w-full"
                  >
                    Zurück zur Startseite
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
